import { Link } from '@inertiajs/react'
import { Calendar, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { BlogPostCard as BlogPostCardType } from '@/types'

type Props = {
  posts: BlogPostCardType[]
  title?: string
}

// Compact list of the latest articles, shown in the blog hero's right column.
// Mirrors the home page's EventListPreview, but driven by real post data.
export default function BlogPostListPreview({ posts, title = 'Latest articles' }: Props) {
  const items = posts.slice(0, 4)
  if (items.length === 0) return null

  return (
    <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
      <div className="border-b border-black/5 p-4">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
      </div>
      <ul className="divide-y divide-black/5">
        {items.map((post) => (
          <li key={post.id}>
            <Link href={`/blog/${post.slug}`} className="group flex items-center gap-3 px-4 py-3 transition hover:bg-muted/50">
              <span className="size-11 shrink-0 overflow-hidden rounded-lg bg-primary/10">
                {post.cover_image_url ? (
                  <img src={post.cover_image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center">
                    <Calendar className="size-5 text-primary/40" />
                  </span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium transition group-hover:text-primary">{post.title}</p>
                <div className="mt-1 flex items-center gap-1.5">
                  {post.category && (
                    <Badge className="bg-primary/10 text-primary">{post.category.name}</Badge>
                  )}
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="size-3" />
                    {post.reading_time_minutes} min
                  </span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
